/* AI Quality Program — full-program flow diagram.
   Ported from the design bundle's programflow.jsx. Three zones:
   YOUR PROJECT (sources) → THE PROGRAM (AI Quality Team + record) → YOU (outputs).
   Continuous packet flow is CSS keyframes; discrete beats are JS-scheduled.
   Self-contained: injects its own pf- styles, reads Orbix tokens via var(). */

import React, { useMemo, useState, useEffect } from 'react';

const PF = {
  W: 1240, H: 540,
  SRC_X: 150, SRC_EDGE: 228,
  BOX_L: 470, BOX_R: 758, BOX_T: 80, BOX_B: 472,
  SPH_X: 614, SPH_Y: 224,
  REC_X: 614, REC_Y: 418, REC_TOP: 396,
  CLI_X: 1050, CLI_EDGE: 960,
  LANES: [120, 270, 420],
};

const PF_SOURCES = [
  { id: 'fabA', title: 'Fab Shop A', sub: 'Off-site', y: 120,
    icon: 'factory',
    packets: [{ kind: 'img', sev: 'ok', delay: 0.0 }, { kind: 'scan', sev: 'ok', delay: 5.6 }] },
  { id: 'fabB', title: 'Fab Shop B', sub: 'Off-site', y: 270,
    icon: 'factory',
    packets: [{ kind: 'doc', sev: 'ok', delay: 1.9 }, { kind: 'img', sev: 'flag', delay: 7.3 }] },
  { id: 'site', title: 'Site Crews', sub: 'On-site', y: 420,
    icon: 'crew',
    packets: [{ kind: 'doc', sev: 'ok', delay: 3.4 }, { kind: 'scan', sev: 'ok', delay: 9.0 }] },
];
const PF_CYCLE = 12;

const PF_ALERTS = [
  { from: 'fabB', label: 'REJECT SPIKE · FAB SHOP B' },
  { from: 'sphere', label: 'BUDGET TRENDING OVER' },
  { from: 'site', label: 'INDICATION CLUSTER · SITE CREWS' },
  { from: 'sphere', label: 'CERT EXPIRING · 3 TECHNICIANS' },
];

function pfOrigin(from) {
  if (from === 'fabA') return [PF.SRC_EDGE, 120];
  if (from === 'fabB') return [PF.SRC_EDGE, 270];
  if (from === 'site') return [PF.SRC_EDGE, 420];
  return [PF.BOX_R, 250];
}

function PfGlyph({ kind, stroke = '#0B0820' }) {
  const s = { width: 16, height: 16, viewBox: '0 0 24 24', fill: 'none', stroke, strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' };
  if (kind === 'doc') return <svg {...s}><path d="M7 4h8l4 4v12H7z" /><path d="M15 4v4h4" /><path d="M10 13h6M10 16h4" /></svg>;
  if (kind === 'scan') return <svg {...s}><path d="M4 8V5a1 1 0 011-1h3M16 4h3a1 1 0 011 1v3M20 16v3a1 1 0 01-1 1h-3M8 20H5a1 1 0 01-1-1v-3" /><path d="M4 12h16" /></svg>;
  return <svg {...s}><rect x="4" y="5" width="16" height="14" rx="1.5" /><circle cx="9" cy="10" r="1.8" /><path d="M4 17l5-4 4 3 3-3 4 4" /></svg>;
}

function PfNodeIcon({ kind }) {
  const s = { width: 30, height: 30, viewBox: '0 0 24 24', fill: 'none', strokeWidth: 1.8, strokeLinecap: 'round', strokeLinejoin: 'round' };
  if (kind === 'factory') return <svg {...s} stroke="#C6ACFF"><path d="M3 21h18M4 21V11l5 3V11l5 3V7l5 3v11" /><path d="M8 21v-4M13 21v-4" /></svg>;
  if (kind === 'crew') return <svg {...s} stroke="#C6ACFF"><circle cx="9" cy="8" r="3" /><path d="M2.5 20a6.5 6.5 0 0 1 13 0" /><path d="M17 8.2a3 3 0 0 1 0 5.6M16.5 20a6.5 6.5 0 0 0-2.3-5" /></svg>;
  if (kind === 'alert') return <svg {...s} stroke="#FFC27D"><path d="M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.7 21a2 2 0 0 1-3.4 0" /></svg>;
  if (kind === 'report') return <svg {...s} stroke="#6EE7B7"><rect x="3" y="5" width="18" height="14" rx="2" /><path d="M3 7l9 6 9-6" /></svg>;
  if (kind === 'access') return <svg {...s} stroke="#9AE6FF"><rect x="3" y="4" width="18" height="13" rx="1.5" /><path d="M3 9h18M8 21h8M12 17v4" /></svg>;
  if (kind === 'record') return <svg {...s} stroke="#6EE7B7"><ellipse cx="12" cy="6" rx="8" ry="2.6" /><path d="M4 6v6c0 1.5 3.6 2.6 8 2.6s8-1.1 8-2.6V6" /><path d="M4 12v6c0 1.5 3.6 2.6 8 2.6s8-1.1 8-2.6v-6" /></svg>;
  return null;
}

const PF_INSIGHTS = ['bars', 'line', 'donut'];
function PfMiniChart({ kind }) {
  if (kind === 'bars') {
    const bars = [[5, 13], [14, 19], [23, 9], [32, 23], [41, 15], [50, 11]];
    return (
      <svg width="62" height="28" viewBox="0 0 62 28" aria-hidden="true">
        {bars.map(([x, h], i) => (
          <rect key={i} x={x} y={26 - h} width="7" height={h} rx="1.5" fill="#6EE7B7" opacity={0.55 + i * 0.07} />
        ))}
      </svg>
    );
  }
  if (kind === 'line') {
    return (
      <svg width="62" height="28" viewBox="0 0 62 28" fill="none" aria-hidden="true">
        <polyline points="2,22 13,13 23,17 33,6 44,11 60,3" stroke="#6EE7B7" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="33" cy="6" r="2.6" fill="#6EE7B7" />
      </svg>
    );
  }
  return (
    <svg width="30" height="30" viewBox="0 0 30 30" fill="none" aria-hidden="true">
      <circle cx="15" cy="15" r="10.5" stroke="rgba(110,231,183,0.22)" strokeWidth="5" />
      <circle cx="15" cy="15" r="10.5" stroke="#6EE7B7" strokeWidth="5" strokeLinecap="round"
        strokeDasharray="46 66" transform="rotate(-90 15 15)" />
    </svg>
  );
}

function ClientNode({ kind, y, title, sub, pulse, live }) {
  return (
    <div className={'pf-node pf-client pf-client--' + kind + (live ? ' pf-client--live' : '') + (pulse ? ' pf-client--pulse' : '')}
      style={{ left: PF.CLI_X - 92, top: y - 50 }}>
      <div className="pf-client__icon"><PfNodeIcon kind={kind} /></div>
      <div className="pf-client__tx">
        <div className="pf-node__title">{title}</div>
        <div className="pf-node__sub">{sub}</div>
      </div>
      {live && <div className="pf-client__glow" />}
    </div>
  );
}

function ProgramFlow() {
  const [alerts, setAlerts] = useState([]);
  const [flick, setFlick] = useState(null);
  const [report, setReport] = useState(null);
  const [boxPulse, setBoxPulse] = useState(null);

  useEffect(() => {
    let ai = 0;
    const timers = [];
    function fireAlert() {
      const a = PF_ALERTS[ai % PF_ALERTS.length];
      ai += 1;
      const isSource = a.from !== 'sphere';
      const [sx, sy] = pfOrigin(a.from);
      const key = 'a' + Date.now() + '-' + ai;
      setAlerts((list) => [...list, { key, label: a.label, isSource, sx, sy }]);
      function pulse() {
        setBoxPulse(key);
        timers.push(setTimeout(() => setBoxPulse((p) => (p === key ? null : p)), 780));
      }
      if (isSource) {
        setFlick(a.from);
        timers.push(setTimeout(() => setFlick((f) => (f === a.from ? null : f)), 1500));
        timers.push(setTimeout(pulse, 560));
      } else {
        pulse();
      }
      timers.push(setTimeout(() => setAlerts((list) => list.filter((x) => x.key !== key)), 3600));
    }
    function fireReport() {
      const key = 'r' + Date.now();
      setReport(key);
      timers.push(setTimeout(() => setReport((r) => (r === key ? null : r)), 3600));
    }
    timers.push(setTimeout(fireAlert, 1600));
    const alertIv = setInterval(fireAlert, 5200);
    const reportIv = setInterval(fireReport, 12800);
    timers.push(setTimeout(fireReport, 6800));
    return () => { clearInterval(alertIv); clearInterval(reportIv); timers.forEach(clearTimeout); };
  }, []);

  const flowCss = useMemo(() => pfCss(), []);

  return (
    <div className="pf-wrap">
      <div className="pf-stage" style={{ width: PF.W, height: PF.H }}>
        <div className="pf-zonehead" style={{ left: PF.SRC_X }}>Your project</div>
        <div className="pf-zonehead" style={{ left: PF.SPH_X }}>The program</div>
        <div className="pf-zonehead pf-zonehead--you" style={{ left: PF.CLI_X }}>You</div>

        <div className={'pf-box' + (boxPulse ? ' pf-box--pulse' : '')} style={{ left: PF.BOX_L, top: PF.BOX_T, width: PF.BOX_R - PF.BOX_L, height: PF.BOX_B - PF.BOX_T }} />

        <svg className="pf-lines" viewBox={`0 0 ${PF.W} ${PF.H}`} width={PF.W} height={PF.H} aria-hidden="true">
          {PF.LANES.map((y) => (
            <line key={'in' + y} x1={PF.SRC_EDGE} y1={y} x2={PF.BOX_L} y2={y} className="pf-line pf-line--in" />
          ))}
          <line x1={PF.BOX_R} y1={120} x2={PF.CLI_EDGE} y2={120} className="pf-line pf-line--out" />
          <line x1={PF.BOX_R} y1={270} x2={PF.CLI_EDGE} y2={270} className="pf-line pf-line--out" />
          <line x1={PF.BOX_R} y1={420} x2={PF.CLI_EDGE} y2={420} className="pf-line pf-line--live" />
          <line x1={PF.SPH_X} y1={PF.SPH_Y + 84} x2={PF.REC_X} y2={PF.REC_TOP - 8} className="pf-line pf-line--rec" />
        </svg>

        <div className="pf-mlabel" style={{ left: 348, top: 96 }}>Inspections · reports · data</div>
        <div className="pf-mlabel pf-mlabel--ok" style={{ left: PF.REC_X, top: PF.REC_Y + 36, transform: 'translateX(-50%)' }}>Verified · saved to the record</div>

        {PF_SOURCES.map((s) => (
          <div key={s.id}
            className={'pf-node pf-source' + (flick === s.id ? ' pf-source--flicker' : '')}
            style={{ left: PF.SRC_X - 76, top: s.y - 43 }}>
            <div className="pf-source__icon"><PfNodeIcon kind={s.icon} /></div>
            <div className="pf-source__tx">
              <div className="pf-node__title">{s.title}</div>
              <div className="pf-node__sub">{s.sub}</div>
            </div>
            {flick === s.id && <div className="pf-source__ping" />}
          </div>
        ))}

        <div className="pf-record" style={{ left: PF.REC_X - 78, top: PF.REC_Y - 32 }}>
          <PfNodeIcon kind="record" />
          <span>System of record</span>
        </div>

        <div className="pf-sphere" style={{ left: PF.SPH_X - 100, top: PF.SPH_Y - 100 }}>
          <div className="pf-sphere__pulse pf-sphere__pulse--1" />
          <div className="pf-sphere__pulse pf-sphere__pulse--2" />
          <div className="pf-sphere__orb">
            <div className="pf-sphere__hl" />
            <div className="pf-sphere__label">AI Quality<br />Team</div>
          </div>
        </div>

        {PF_SOURCES.flatMap((s) =>
          s.packets.map((p, i) => (
            <div key={s.id + i} className="pf-packet"
              style={{
                animation: `pf-${p.sev === 'ok' ? 'ok' : 'flag'}-${s.id} ${PF_CYCLE}s linear infinite`,
                animationDelay: `${-p.delay}s`,
              }}>
              <PfGlyph kind={p.kind} />
            </div>
          ))
        )}

        <ClientNode kind="alert" y={120} title="Real-time alerts" sub="Early warning" />
        <ClientNode kind="report" y={270} title="Biweekly report" sub="In your inbox" pulse={!!report} />
        <ClientNode kind="access" y={420} title="Live access" sub="Always on" live />

        {alerts.map((a) => {
          const leg2Delay = a.isSource ? 0.72 : 0.12;
          const chipDelay = leg2Delay + 0.55;
          return (
            <React.Fragment key={a.key}>
              {a.isSource && (
                <div className="pf-fly pf-fly--intake"
                  style={{ '--sx': a.sx + 'px', '--sy': a.sy + 'px', '--ex': (PF.BOX_L + 14) + 'px', '--ey': a.sy + 'px' }} />
              )}
              <div className="pf-fly pf-fly--alert"
                style={{ '--sx': PF.BOX_R + 'px', '--sy': '120px', '--ex': (PF.CLI_EDGE + 4) + 'px', '--ey': '120px', animationDelay: leg2Delay + 's' }} />
              <div className="pf-chip" style={{ left: PF.CLI_X, top: 180, animationDelay: chipDelay + 's' }}>
                <span className="pf-chip__dot" />{a.label}
              </div>
            </React.Fragment>
          );
        })}
        {report && (
          <div className="pf-fly pf-fly--report"
            style={{ '--sx': PF.BOX_R + 'px', '--sy': '286px', '--ex': PF.CLI_EDGE + 4 + 'px', '--ey': '270px' }} />
        )}
        {report && PF_INSIGHTS.map((k, i) => (
          <div key={report + k} className={'pf-insight pf-insight--' + k}
            style={{ left: PF.CLI_X + 102, top: 224 + i * 50, animationDelay: (1.35 + i * 0.16) + 's' }}>
            <PfMiniChart kind={k} />
          </div>
        ))}

        <style dangerouslySetInnerHTML={{ __html: flowCss }} />
      </div>

      <div className="pf-legend">
        <span className="pf-legend__item"><i style={{ background: '#A78BFA' }} />Incoming work</span>
        <span className="pf-legend__item"><i style={{ background: '#EF4444' }} />Flagged · back to the field</span>
        <span className="pf-legend__item"><i style={{ background: '#10B981' }} />Verified · saved</span>
        <span className="pf-legend__item"><i style={{ background: '#FFC27D' }} />Custom alert · straight to you</span>
      </div>
    </div>
  );
}

function pfCss() {
  const off = 16;
  const at = (x, y) => `translate(${x - off}px, ${y - off}px)`;
  const P = PF;

  const flows = PF_SOURCES.map((s) => {
    const start = [P.SRC_EDGE, s.y];
    const entry = [P.BOX_L + 6, s.y];
    const sph = [P.SPH_X, P.SPH_Y + 66];
    const rec = [P.REC_X, P.REC_TOP];
    const PUR = '#A78BFA', GRN = '#10B981', RED = '#EF4444';
    const shP = `0 0 16px ${PUR}, 0 4px 8px rgba(0,0,0,.4)`;
    const shG = `0 0 20px ${GRN}, 0 4px 8px rgba(0,0,0,.4)`;
    const shR = `0 0 20px ${RED}, 0 4px 8px rgba(0,0,0,.4)`;
    return `
    @keyframes pf-ok-${s.id} {
      0%   { transform: ${at(...start)}; opacity: 0; background: ${PUR}; box-shadow: ${shP}; }
      4%   { opacity: 1; }
      30%  { transform: ${at(...entry)}; background: ${PUR}; box-shadow: ${shP}; }
      40%  { transform: ${at(...sph)}; opacity: 1; background: ${PUR}; }
      44%  { transform: ${at(...sph)}; background: ${GRN}; box-shadow: ${shG}; }
      57%  { transform: ${at(...rec)}; opacity: 1; background: ${GRN}; box-shadow: ${shG}; }
      63%  { transform: ${at(...rec)}; opacity: 0; background: ${GRN}; }
      100% { transform: ${at(...rec)}; opacity: 0; }
    }
    @keyframes pf-flag-${s.id} {
      0%   { transform: ${at(...start)}; opacity: 0; background: ${PUR}; box-shadow: ${shP}; }
      4%   { opacity: 1; }
      24%  { transform: ${at(...entry)}; background: ${PUR}; box-shadow: ${shP}; }
      31%  { transform: ${at(...sph)}; background: ${RED}; box-shadow: ${shR}; }
      39%  { transform: ${at(...entry)}; background: ${RED}; box-shadow: ${shR}; }
      47%  { transform: ${at(...start)}; opacity: 1; background: ${RED}; box-shadow: ${shR}; }
      51%  { transform: ${at(...start)}; background: ${PUR}; box-shadow: ${shP}; }
      70%  { transform: ${at(...entry)}; background: ${PUR}; box-shadow: ${shP}; }
      77%  { transform: ${at(...sph)}; background: ${GRN}; box-shadow: ${shG}; }
      91%  { transform: ${at(...rec)}; opacity: 1; background: ${GRN}; box-shadow: ${shG}; }
      95%  { transform: ${at(...rec)}; opacity: 0; }
      100% { transform: ${at(...rec)}; opacity: 0; }
    }`;
  }).join('\n');

  return `
  .pf-wrap { display: flex; flex-direction: column; align-items: center; gap: 18px; }
  .pf-stage { position: relative; margin: 0 auto; }

  .pf-zonehead {
    position: absolute; top: 0; transform: translateX(-50%);
    font-family: var(--orbix-font-ui); font-weight: 800; font-size: 12px;
    letter-spacing: 0.2em; text-transform: uppercase; color: var(--orbix-fg-on-dark-mute);
  }
  .pf-zonehead--you { color: var(--sigma-green-bright); }

  .pf-box {
    position: absolute; border-radius: 20px;
    background: linear-gradient(180deg, rgba(129,107,255,0.07), rgba(255,255,255,0.012));
    border: 1px solid rgba(129,107,255,0.24);
    box-shadow: inset 0 0 70px rgba(129,107,255,0.07);
  }
  .pf-box--pulse { animation: pf-boxpulse 0.78s ease-out; }
  @keyframes pf-boxpulse {
    0%   { border-color: rgba(129,107,255,0.24); box-shadow: inset 0 0 70px rgba(129,107,255,0.07); }
    40%  { border-color: #FFC27D; box-shadow: inset 0 0 80px rgba(255,194,125,0.18), 0 0 34px rgba(255,194,125,0.32); }
    100% { border-color: rgba(129,107,255,0.24); box-shadow: inset 0 0 70px rgba(129,107,255,0.07); }
  }

  .pf-lines { position: absolute; inset: 0; pointer-events: none; overflow: visible; }
  .pf-line { fill: none; }
  .pf-line--in  { stroke: rgba(198,172,255,0.22); stroke-width: 2; stroke-dasharray: 4 6; }
  .pf-line--rec { stroke: rgba(110,231,183,0.3); stroke-width: 2; stroke-dasharray: 4 6; }
  .pf-line--out { stroke: rgba(255,255,255,0.1); stroke-width: 2; stroke-dasharray: 3 7; }
  .pf-line--live {
    stroke: rgba(154,230,255,0.5); stroke-width: 2.4; stroke-dasharray: 7 7;
    animation: pf-dash 1.1s linear infinite;
  }
  @keyframes pf-dash { to { stroke-dashoffset: -28; } }

  .pf-mlabel {
    position: absolute; transform: translateX(-50%);
    font-family: var(--orbix-font-ui); font-size: 11px; letter-spacing: 0.06em;
    color: rgba(255,255,255,0.5); white-space: nowrap;
  }
  .pf-mlabel--ok { color: #6EE7B7; }

  .pf-node { position: absolute; box-sizing: border-box; }
  .pf-node__title { font-family: var(--orbix-font-display); font-weight: 700; font-size: 16px; color: #fff; line-height: 1.1; }
  .pf-node__sub { font-family: var(--orbix-font-ui); font-size: 11px; letter-spacing: 0.05em; color: var(--orbix-fg-on-dark-mute); margin-top: 3px; }

  .pf-source {
    width: 152px; height: 86px; display: flex; align-items: center; gap: 13px; padding: 0 16px;
    background: rgba(255,255,255,0.045); border: 1px solid rgba(198,172,255,0.26);
    border-radius: var(--orbix-r-md);
  }
  .pf-source__icon { width: 40px; height: 40px; flex-shrink: 0; display: inline-flex; align-items: center; justify-content: center; }
  .pf-source--flicker { border-color: #FFC27D; box-shadow: 0 0 0 1px #FFC27D, 0 0 28px rgba(255,194,125,0.4); animation: pf-flicker 0.5s ease-in-out 2; }
  @keyframes pf-flicker { 0%,100% { background: rgba(255,255,255,0.045); } 50% { background: rgba(255,194,125,0.16); } }
  .pf-source__ping { position: absolute; inset: -1px; border-radius: var(--orbix-r-md); border: 1px solid #FFC27D; animation: pf-ping 1.4s ease-out infinite; }
  @keyframes pf-ping { 0% { transform: scale(1); opacity: 0.8; } 100% { transform: scale(1.12); opacity: 0; } }

  .pf-record {
    position: absolute; width: 156px; height: 60px; display: flex; flex-direction: column;
    align-items: center; justify-content: center; gap: 5px;
    background: rgba(110,231,183,0.06); border: 1px solid rgba(110,231,183,0.26);
    border-radius: var(--orbix-r-md); opacity: 0.92;
  }
  .pf-record span { font-family: var(--orbix-font-ui); font-weight: 600; font-size: 11px; letter-spacing: 0.08em; text-transform: uppercase; color: #6EE7B7; }

  .pf-sphere { position: absolute; width: 200px; height: 200px; }
  .pf-sphere__pulse { position: absolute; inset: 18px; border-radius: 50%; border: 1px solid rgba(129,107,255,0.5); }
  .pf-sphere__pulse--1 { animation: pf-spulse 3s ease-out infinite; }
  .pf-sphere__pulse--2 { animation: pf-spulse 3s ease-out infinite 1.5s; }
  @keyframes pf-spulse { 0% { transform: scale(0.9); opacity: 0.7; } 100% { transform: scale(1.28); opacity: 0; } }
  .pf-sphere__orb {
    position: absolute; inset: 24px; border-radius: 50%;
    background: radial-gradient(circle at 34% 30%, #C4B5FD 0%, #6C5CE0 42%, #2E2A63 100%);
    box-shadow: 0 0 50px rgba(108,92,224,0.6), inset -14px -14px 30px rgba(30,22,70,0.8);
    display: flex; align-items: center; justify-content: center; overflow: hidden;
  }
  .pf-sphere__hl { position: absolute; top: 16%; left: 20%; width: 38%; height: 30%; border-radius: 50%; background: radial-gradient(circle, rgba(255,255,255,0.55), transparent 70%); filter: blur(3px); }
  .pf-sphere__label { position: relative; font-family: var(--orbix-font-display); font-weight: 700; font-size: 18px; line-height: 1.15; text-align: center; color: #fff; text-shadow: 0 2px 8px rgba(0,0,0,0.5); }

  .pf-packet { position: absolute; left: 0; top: 0; width: 32px; height: 32px; border-radius: 8px; display: grid; place-items: center; will-change: transform; z-index: 4; }

  .pf-client {
    width: 184px; height: 100px; display: flex; align-items: center; gap: 13px; padding: 0 18px;
    background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.12);
    border-radius: var(--orbix-r-md);
  }
  .pf-client__icon { width: 42px; height: 42px; flex-shrink: 0; border-radius: 10px; display: inline-flex; align-items: center; justify-content: center; }
  .pf-client--alert  .pf-client__icon { background: rgba(255,194,125,0.14); }
  .pf-client--report .pf-client__icon { background: rgba(110,231,183,0.14); }
  .pf-client--access .pf-client__icon { background: rgba(154,230,255,0.14); }
  .pf-client--alert  { border-color: rgba(255,194,125,0.3); }
  .pf-client--report { border-color: rgba(110,231,183,0.3); }
  .pf-client--live { border-color: rgba(154,230,255,0.4); box-shadow: 0 0 30px rgba(154,230,255,0.16); }
  .pf-client__glow { position: absolute; inset: 0; border-radius: var(--orbix-r-md); box-shadow: inset 0 0 22px rgba(154,230,255,0.18); animation: pf-liveglow 2.6s ease-in-out infinite; pointer-events: none; }
  @keyframes pf-liveglow { 0%,100% { opacity: 0.5; } 50% { opacity: 1; } }
  .pf-client--pulse { animation: pf-rpulse 0.6s ease-out 2; }
  @keyframes pf-rpulse { 0%,100% { box-shadow: 0 0 0 rgba(110,231,183,0); } 50% { box-shadow: 0 0 32px rgba(110,231,183,0.5); border-color: #6EE7B7; } }

  .pf-insight {
    position: absolute; width: 84px; height: 42px; box-sizing: border-box;
    display: flex; align-items: center; justify-content: center;
    border-radius: 9px; background: rgba(110,231,183,0.1);
    border: 1px solid rgba(110,231,183,0.36); box-shadow: 0 8px 22px rgba(0,0,0,0.42);
    z-index: 7; animation: pf-insight 1.9s ease-out both;
  }
  @keyframes pf-insight {
    0%   { opacity: 0; transform: translateX(-12px) scale(0.6); }
    16%  { opacity: 1; transform: translateX(0) scale(1.04); }
    24%  { transform: translateX(0) scale(1); }
    72%  { opacity: 1; transform: translateX(0) scale(1); }
    100% { opacity: 0; transform: translateX(8px) scale(0.92); }
  }

  .pf-fly { position: absolute; left: 0; top: 0; width: 14px; height: 14px; border-radius: 50%; z-index: 6; will-change: transform; }
  .pf-fly--intake { background: #FFC27D; box-shadow: 0 0 14px #FFC27D; animation: pf-fly 0.7s ease-in both; }
  .pf-fly--alert { background: #FFC27D; box-shadow: 0 0 16px #FFC27D; animation: pf-fly 1s cubic-bezier(.5,0,.2,1) both; }
  .pf-fly--report { background: #6EE7B7; box-shadow: 0 0 16px #6EE7B7; animation: pf-fly 1.5s cubic-bezier(.5,0,.2,1) both; }
  @keyframes pf-fly {
    0%   { transform: translate(var(--sx), var(--sy)) scale(0.5); opacity: 0; }
    12%  { transform: translate(var(--sx), var(--sy)) scale(1); opacity: 1; }
    78%  { transform: translate(var(--ex), var(--ey)) scale(1); opacity: 1; }
    100% { transform: translate(var(--ex), var(--ey)) scale(0.5); opacity: 0; }
  }

  .pf-chip {
    position: absolute; transform: translateX(-50%); z-index: 7;
    display: inline-flex; align-items: center; gap: 8px; white-space: nowrap;
    padding: 8px 14px; border-radius: var(--orbix-r-pill);
    background: rgba(255,194,125,0.14); border: 1px solid rgba(255,194,125,0.5);
    font-family: var(--orbix-font-ui); font-weight: 700; font-size: 12px; letter-spacing: 0.04em; color: #FFD9A8;
    animation: pf-chip 2s ease-in-out both;
  }
  .pf-chip__dot { width: 7px; height: 7px; border-radius: 50%; background: #FFC27D; box-shadow: 0 0 10px #FFC27D; }
  @keyframes pf-chip {
    0%   { opacity: 0; transform: translateX(-50%) translateY(6px) scale(0.96); }
    14%  { opacity: 1; transform: translateX(-50%) translateY(0) scale(1); }
    82%  { opacity: 1; }
    100% { opacity: 0; transform: translateX(-50%) translateY(0) scale(1); }
  }

  .pf-legend { display: flex; flex-wrap: wrap; gap: 22px; }
  .pf-legend__item { display: inline-flex; align-items: center; gap: 9px; font-family: var(--orbix-font-ui); font-size: 13px; color: var(--orbix-fg-on-dark-dim); }
  .pf-legend__item i { width: 11px; height: 11px; border-radius: 3px; display: inline-block; }

  ${flows}
  `;
}

export default ProgramFlow;
